import { createAccount, updateAccount } from "../api/accountService";
import { getAccountType } from "../api/AccountTypeService";
import { getDomains } from "../api/domainService";
import { checkUser } from '../api/AuthService';
import { toast } from "sonner";
import apiClient from "../api/api";
import imageCompression from 'browser-image-compression';

// ─────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────
const MAX_TOTAL_SIZE_BYTES = 80 * 1024 * 1024;   // 80 MB total
const MAX_SINGLE_FILE_MB   = 15;                  // 15 MB par fichier
const MAX_SINGLE_FILE_BYTES = MAX_SINGLE_FILE_MB * 1024 * 1024;

const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

// ─────────────────────────────────────────────
// HELPERS INTERNES
// ─────────────────────────────────────────────

/**
 * Compresse une image si nécessaire, sinon retourne le fichier tel quel.
 */
const compressIfImage = async (file) => {
  if (!file) return null;
  if (!file.type.startsWith('image/')) return file;

  try {
    const compressed = await imageCompression(file, IMAGE_COMPRESSION_OPTIONS);
    return new File([compressed], file.name, { type: compressed.type });
  } catch {
    // En cas d'échec de compression, on envoie l'original
    return file;
  }
};

/**
 * Vérifie la taille totale de tous les fichiers dans un FormData.
 * Lance une erreur explicite si la limite est dépassée.
 */
const assertTotalSizeUnderLimit = (formData) => {
  let totalBytes = 0;

  for (const [, value] of formData.entries()) {
    if (value instanceof File) {
      totalBytes += value.size;
    }
  }

  if (totalBytes > MAX_TOTAL_SIZE_BYTES) {
    const totalMB = (totalBytes / 1024 / 1024).toFixed(1);
    throw new Error(
      `La taille totale des fichiers (${totalMB} MB) dépasse la limite autorisée de ` +
      `${MAX_TOTAL_SIZE_BYTES / 1024 / 1024} MB. Veuillez réduire la taille de vos documents.`
    );
  }
};

/**
 * Vérifie qu'aucun fichier individuel ne dépasse la limite unitaire.
 */
const assertSingleFileSizes = (formData) => {
  for (const [key, value] of formData.entries()) {
    if (value instanceof File && value.size > MAX_SINGLE_FILE_BYTES) {
      throw new Error(
        `Le fichier "${value.name}" (champ: ${key}) fait ` +
        `${(value.size / 1024 / 1024).toFixed(1)} MB et dépasse la limite de ${MAX_SINGLE_FILE_MB} MB par fichier.`
      );
    }
  }
};

// ─────────────────────────────────────────────
// SERVICE PRINCIPAL
// ─────────────────────────────────────────────
const AccountFormService = {

  fetchDomains: async () => {
    try {
      return await getDomains();
    } catch (error) {
      toast.error('Désolé, erreur lors du chargement des domaines.');
      throw error;
    }
  },

  fetchAccountTypes: async () => {
    try {
      return await getAccountType();
    } catch (error) {
      toast.error('Désolé, erreur lors du chargement des types de comptes.');
      throw error;
    }
  },

  fetchUserRole: async (user, setRole, setManagerDomain) => {
    if (!user) return;
    try {
      const email = user.primaryEmailAddress.emailAddress;
      const userData = await checkUser(email);

      if (userData?.isAdmin) {
        setRole("admin");
      } else {
        const storedDomain = localStorage.getItem("selectedDomain");
        if (storedDomain) {
          const domainObj = JSON.parse(storedDomain);
          if (domainObj?.role) {
            setRole(domainObj.role);
            setManagerDomain(domainObj);
            return;
          }
        }
        if (userData?.selectedDomain?.role) {
          setRole(userData.selectedDomain.role);
          setManagerDomain(userData.selectedDomain);
        } else {
          setRole("client");
        }
      }
    } catch (error) {
      toast.error("Erreur lors de la récupération du rôle : " + error.message);
      throw error;
    }
  },

  // ───────────────────────────────────────────
  // SUBMIT PRINCIPAL
  // ───────────────────────────────────────────
  submitForm: async (
    data,
    isUpdateMode,
    initialAccountData,
    user,
    role,
    reset,
    setBackendErrors,
    accountTypes,
    existingFiles,
    connectedAccountId,
    connectedUserDomain
  ) => {
    try {
      const formData = new FormData();
      const isManagedAccount = data.IsManagedAccount || false;

      // ── Champ administré ──────────────────────
      formData.append('IsManagedAccount', isManagedAccount.toString());

      if (isUpdateMode && initialAccountData?.id) {
        formData.append('AccountId', initialAccountData.id.toString());
      }

      // ── Champs texte ──────────────────────────
      const textFields = [
        'companyName', 'company_adress', 'firstName', 'lastName', 'email',
        'phoneNumber', 'address', 'job', 'countryOfBirth', 'cityOfBirth',
        'legalForm', 'isin', 'description', 'preference', 'typeId',
        'numeroCompteBancaire', 'banqueDomiciliation', 'numeroIdentificationFiscale', 'tva',
      ];

      textFields.forEach((field) => {
        if (data[field] !== undefined && data[field] !== null) {
          if (isUpdateMode) {
            formData.append(field, data[field] || '');
          } else if (data[field] !== '') {
            formData.append(field, data[field]);
          }
        }
      });

      // ── Domaine ───────────────────────────────
      if (!isUpdateMode) {
        if (role === "admin") {
          if (!data.domainId) throw new Error("Veuillez sélectionner un domaine");
          formData.append('domainId', data.domainId.toString());
        } else {
          if (!connectedUserDomain?.id)
            throw new Error("Domaine non disponible pour l'utilisateur connecté");
          formData.append('domainId', connectedUserDomain.id.toString());
        }
      }

      // ── Champs numériques ─────────────────────
      const numericFields = ['yearlyRevenue', 'capital', 'totalOutstandingShares', 'cash'];
      numericFields.forEach((field) => {
        if (data[field] !== undefined && data[field] !== null) {
          formData.append(field, data[field].toString());
        } else if (isUpdateMode) {
          formData.append(field, '0');
        }
      });

      // ── Date de naissance ─────────────────────
      if (data.dob) {
        formData.append('dob', new Date(data.dob).toISOString());
      } else if (isUpdateMode) {
        formData.append('dob', '');
      }

      // ── Fichiers (avec compression images) ───
      const fileFields = [
        'picture', 'company_logo', 'proofOfAddress', 'proofOfRevenue',
        'convention', 'Formulaire_OuvertureFile', 'KycFile', 'Convention_OuvertureFile',
        'signatureImage', 'cni', 'rccm', 'statut', 'annonceLegale',
        'procuration', 'attestationImmatriculation', 'photoIdentite',
        'journalAnnoncesLegalesFile', 'referenceBancaireEntrepriseFile', 'banqueDomiciliationFile',
      ];

      for (const field of fileFields) {
        const raw = data[field]?.[0] ?? null;
        if (!raw) continue;

        const fileToUpload = await compressIfImage(raw);
        formData.append(field, fileToUpload);
      }

      // ── Vérifications de taille ───────────────
      // 1. Chaque fichier individuellement
      assertSingleFileSizes(formData);
      // 2. Total de tous les fichiers
      assertTotalSizeUnderLimit(formData);

      // ── Infos créateur (création seulement) ───
      if (!isUpdateMode) {
        if (!connectedAccountId)
          throw new Error("Impossible de déterminer l'utilisateur créateur");

        formData.append('CreatingUserId', connectedAccountId.toString());
        formData.append('is_manager', role === "admin" ? "true" : "false");

        // ── Mandataires (compte administré) ──────
        if (isManagedAccount) {
          const representativeIds  = data.RepresentativeAccountIds || [];
          const mandateTypes       = data.MandateTypes             || [];
          const relationshipTypes  = data.RelationshipTypes        || [];
          const mandateProofFiles  = data.MandateProofFiles        || [];

          if (representativeIds.length === 0)
            throw new Error("Au moins un mandataire est requis pour les comptes administrés");

          representativeIds.forEach((id)   => formData.append('RepresentativeAccountIds', id.toString()));
          mandateTypes.forEach((type)       => formData.append('MandateTypes', type));
          relationshipTypes.forEach((rel)   => formData.append('RelationshipTypes', rel));
          mandateProofFiles.forEach((file)  => { if (file) formData.append('MandateProofFiles', file); });
        }
      }

      // ── Envoi HTTP ────────────────────────────
      const response = isUpdateMode
        ? await AccountFormService._uploadWithProgress(
            'put',
            `/api/accounts/${initialAccountData.id}`,
            formData
          )
        : await AccountFormService._uploadWithProgress('post', '/api/accounts', formData);

      reset();
      return response;

    } catch (error) {
      // Gestion centralisée des erreurs backend
      if (error.response?.data?.Errors) {
        setBackendErrors(error.response.data.Errors);
      } else if (error.response?.data?.Message) {
        toast.error(error.response.data.Message);
      } else if (error.message) {
        toast.error(error.message);
      }
      throw error;
    }
  },

  // ───────────────────────────────────────────
  // UPLOAD AVEC PROGRESSION + TIMEOUT LONG
  // ───────────────────────────────────────────
  /**
   * Envoie un FormData avec :
   *  - Timeout de 10 minutes (connexions instables / gros fichiers)
   *  - Log de progression dans la console (à brancher sur une progress bar si besoin)
   *  - Retry automatique 1 fois en cas d'erreur réseau (pas de 4xx/5xx)
   */
  _uploadWithProgress: async (method, url, formData, retryCount = 0) => {
    try {
      const response = await apiClient[method](url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },

        // ✅ 10 minutes — critique pour connexions lentes + beaucoup de fichiers
        timeout: 10 * 60 * 1000,

        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            // Tu peux remplacer ce console.log par un setState/callback si tu veux afficher une barre
            console.debug(`[Upload] ${pct}% — ${(progressEvent.loaded / 1024 / 1024).toFixed(1)} MB`);
          }
        },
      });

      return response.data;

    } catch (error) {
      // Retry une seule fois si c'est une erreur réseau pure (pas de réponse serveur)
      const isNetworkError = !error.response && error.request;
      if (isNetworkError && retryCount === 0) {
        console.warn('[Upload] Erreur réseau, nouvelle tentative dans 3 secondes...');
        toast.warning('Connexion instable, nouvelle tentative en cours...');
        await new Promise((r) => setTimeout(r, 3000));
        return AccountFormService._uploadWithProgress(method, url, formData, 1);
      }
      throw error;
    }
  },

  // ───────────────────────────────────────────
  // SUPPRESSION DE FICHIER
  // ───────────────────────────────────────────
  handleRemoveFile: async (accountId, fieldName, setExistingFiles, showLoader, hideLoader) => {
    if (!accountId) {
      toast.error("ID du compte manquant");
      return { success: false, error: "ID du compte manquant" };
    }

    try {
      showLoader({ message: "Suppression du fichier en cours..." });
      const response = await apiClient.delete(`/api/Accounts/${accountId}/remove-file`, {
        data: { fieldName: AccountFormService.getBackendFieldName(fieldName) },
      });

      const ok = response.data?.Success === true || response.data?.success === true;
      if (ok) {
        setExistingFiles((prev) => ({
          ...prev,
          [`${fieldName}Url`]:      null,
          [`${fieldName}FileName`]: null,
        }));
        toast.success(response.data.Message || response.data.message || "Fichier supprimé avec succès");
        return { success: true, data: response.data };
      } else {
        toast.error(response.data.Message || response.data.message || "Erreur lors de la suppression");
        return { success: false, error: response.data.Message || "Erreur inconnue" };
      }
    } catch (error) {
      let msg = "Erreur lors de la suppression du fichier";
      if (error.response) {
        const { status, data } = error.response;
        if (status === 404) msg = "Fichier non trouvé ou déjà supprimé";
        else if (status === 403) msg = "Permissions insuffisantes";
        else if (status === 500) msg = "Erreur serveur";
        else if (data?.Message) msg = data.Message;
        else if (data?.message) msg = data.message;
      } else if (error.request) {
        msg = "Pas de réponse du serveur. Vérifiez votre connexion.";
      }
      toast.error(msg);
      return { success: false, error: msg };
    } finally {
      hideLoader();
    }
  },

  // ───────────────────────────────────────────
  // MAPPING NOMS DE CHAMPS BACKEND
  // ───────────────────────────────────────────
  getBackendFieldName: (fieldName) => {
    const map = {
      picture:                       'picture',
      company_logo:                  'company_logo',
      proofOfAddress:                'proofofaddress',
      proofOfRevenue:                'proofofrevenue',
      signatureImage:                'signatureimage',
      cni:                           'cni',
      rccm:                          'rccm',
      procuration:                   'procuration',
      attestationImmatriculation:    'attestationimmatriculation',
      statut:                        'statut',
      annonceLegale:                 'annoncelegale',
      photoIdentite:                 'photoidentite',
      journalAnnoncesLegalesFile:    'journalannonceslegales',
      referenceBancaireEntrepriseFile:'referencebancaireentreprise',
      banqueDomiciliationFile:       'banquedomiciliation',
      convention:                    'convention_ouverture',
      Convention_OuvertureFile:      'convention_ouverture',
      KycFile:                       'kyc',
      Formulaire_OuvertureFile:      'formulaire_ouverture',
    };
    return map[fieldName] ?? fieldName;
  },

  // ───────────────────────────────────────────
  // VALIDATIONS
  // ───────────────────────────────────────────
  validateEmail: (email, setValidEmail, setErrors) => {
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    setValidEmail(valid);
    setErrors((prev) => ({
      ...prev,
      email: valid ? '' : 'Adresse email invalide',
    }));
  },

  validateAge: (dob, setValidDob, setErrors, setIsMinor = null) => {
    if (!dob) {
      setValidDob(false);
      if (setIsMinor) setIsMinor(false);
      return { isValid: false, age: null, isMinor: false };
    }

    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;

    setValidDob(true);
    setErrors((prev) => ({ ...prev, dob: '' }));
    if (setIsMinor) setIsMinor(age < 18);

    return { isValid: true, age, isMinor: age < 18 };
  },

  validateDob: (dob, setValidDob, setErrors, setIsMinor = null) =>
    AccountFormService.validateAge(dob, setValidDob, setErrors, setIsMinor),

  validateFile: (fileList, fieldName, allowedTypes, maxSize, setErrors) => {
    if (!fileList || fileList.length === 0) {
      setErrors((prev) => ({ ...prev, [fieldName]: 'Veuillez sélectionner un fichier' }));
      return false;
    }
    const file = fileList[0];
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: `Type non autorisé. Acceptés : ${allowedTypes.join(', ')}`,
      }));
      return false;
    }
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: `Fichier trop lourd (max ${maxSize / 1024 / 1024} Mo)`,
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    return true;
  },

  // ───────────────────────────────────────────
  // CHAMPS REQUIS & VISIBILITÉ PAR TYPE
  // ───────────────────────────────────────────
  getRequiredFieldsForType: (accountTypeName) => {
    if (typeof accountTypeName !== 'string') return [];
    const name = accountTypeName.toLowerCase();

    if (name === "particulier") {
      return [
        "email", "firstName", "lastName", "countryOfBirth", "cityOfBirth",
        "picture", "dob", "address", "cni", "numeroIdentificationFiscale",
      ];
    }

    if (["entreprise","assurance","banques","association","autres entreprises","autres","opc","opcvm","fia"].includes(name)) {
      return [
        "companyName", "company_adress", "capital", "journalAnnoncesLegalesFile",
        "company_logo", "banqueDomiciliationFile", "referenceBancaireEntrepriseFile",
        "statut", "attestationImmatriculation", "annonceLegale", "legalForm",
      ];
    }

    return ["phoneNumber"];
  },

  shouldShowField: (fieldName, accountTypeName, isParticulier, isEntrepriseType) => {
    const alwaysVisible = ['phoneNumber', 'description', 'preference'];
    if (alwaysVisible.includes(fieldName)) return true;

    if (isParticulier) {
      return [
        'email', 'firstName', 'lastName', 'dob', 'countryOfBirth', 'cityOfBirth',
        'address', 'job', 'yearlyRevenue', 'cash', 'signatureImage', 'proofOfRevenue',
        'numeroIdentificationFiscale', 'photoIdentite', 'cni', 'proofOfAddress',
        'picture', 'tva', 'Convention_OuvertureFile', 'KycFile', 'Formulaire_OuvertureFile',
      ].includes(fieldName);
    }

    if (isEntrepriseType) {
      return [
        'email',
        'companyName', 'company_adress', 'capital', 'legalForm', 'yearlyRevenue', 'company_logo',
        'cash', 'numeroCompteBancaire', 'banqueDomiciliationFile', 'signatureImage', 'rccm',
        'journalAnnoncesLegalesFile', 'referenceBancaireEntrepriseFile', 'statut', 'isin',
        'attestationImmatriculation', 'annonceLegale', 'procuration', 'proofOfRevenue',
        'tva', 'Convention_OuvertureFile', 'KycFile', 'Formulaire_OuvertureFile',
      ].includes(fieldName);
    }

    return true;
  },
};

export default AccountFormService;